import { Box, Avatar, IconButton, Typography, Paper, Collapse, Badge } from "@mui/material";
import { ExpandLess, ExpandMore, NotificationsActive } from "@mui/icons-material";
import { useStore, getStore, useHasHydrated } from "../../store/store";
import { AssistantItem } from "./AssistantItem";
import { useMemo, useEffect, useRef } from "react";

const AVATAR_URL = "http://backend.neuroimprove.pt/assets/99d1ed12-f920-4182-b801-0423c2cd0c8f";

export const AssistantDialog = () => {
    const { assistantDialog } = useStore();
    const hasHydrated = useHasHydrated();
    const hasItems = assistantDialog.items.length > 0;
    const previousItemsCountRef = useRef(assistantDialog.items.length);
    const hasHydratedAndLoadedRef = useRef(false);

    const sortedItems = useMemo(() => {
        const items = [...assistantDialog.items];
        items.sort(
            (a, b) =>
                Number(b.type === "kiosk_callback") - Number(a.type === "kiosk_callback") ||
                a.clientName.localeCompare(b.clientName)
        );
        return items;
    }, [assistantDialog.items]);

    // Mark as ready for auto-expand after hydration and first items load
    useEffect(() => {
        if (hasHydrated && assistantDialog.items.length > 0 && !hasHydratedAndLoadedRef.current) {
            hasHydratedAndLoadedRef.current = true;
            // Only set previousCount if minimized (respect user's choice on page load)
            // If not minimized, leave previousCount at 0 so auto-expand can trigger
            if (assistantDialog.isMinimized) {
                previousItemsCountRef.current = assistantDialog.items.length;
            }
        }
    }, [hasHydrated, assistantDialog.items.length, assistantDialog.isMinimized]);

    // Auto-expand when new items are added (only after initial hydration and load)
    useEffect(() => {
        if (!hasHydratedAndLoadedRef.current) return;

        const currentCount = assistantDialog.items.length;
        const previousCount = previousItemsCountRef.current;

        if (currentCount > previousCount && assistantDialog.isMinimized) {
            getStore().assistantDialog.toggleMinimized();
        }

        previousItemsCountRef.current = currentCount;
    }, [assistantDialog.items.length, assistantDialog.isMinimized]);

    if (!hasItems) return null;

    // Minimized compact view
    if (assistantDialog.isMinimized) {
        return (
            <Paper
                elevation={8}
                onClick={assistantDialog.toggleMinimized}
                sx={{
                    position: "fixed",
                    bottom: 40,
                    right: 24,
                    borderRadius: 3,
                    overflow: "hidden",
                    zIndex: 1300,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    pl: 2,
                    pr: 2,
                    py: 2,
                    background: "linear-gradient(135deg, #44749a 0%, #2d5a7b 100%)",
                    color: "white",
                    "&:hover": {
                        transform: "scale(1.02)",
                    },
                    transition: "transform 0.2s",
                }}
            >
                <Badge
                    badgeContent={assistantDialog.items.length}
                    color="error"
                    overlap="circular"
                >
                    <NotificationsActive fontSize="small" />
                </Badge>
                <Typography variant="body2" fontWeight={500} sx={{ ml: 0.5 }}>
                    Assistente
                </Typography>
                <ExpandLess fontSize="small" />
            </Paper>
        );
    }

    // Expanded view
    return (
        <Paper
            elevation={8}
            sx={{
                position: "fixed",
                bottom: 40,
                right: 24,
                width: 380,
                maxHeight: "70vh",
                borderRadius: 4,
                overflow: "hidden",
                zIndex: 1300,
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    background: "linear-gradient(135deg, #44749a 0%, #2d5a7b 100%)",
                    color: "white",
                    cursor: "pointer",
                }}
                onClick={assistantDialog.toggleMinimized}
            >
                <Badge
                    badgeContent={assistantDialog.items.length}
                    color="error"
                    overlap="circular"
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <Avatar
                        src={AVATAR_URL}
                        alt="Assistente"
                        sx={{
                            width: 48,
                            height: 48,
                            border: "2px solid white",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                        }}
                    />
                </Badge>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Assistente
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        {assistantDialog.items.length} {assistantDialog.items.length === 1 ? "alerta" : "alertas"}
                    </Typography>
                </Box>
                <IconButton size="small" sx={{ color: "white" }}>
                    <ExpandMore />
                </IconButton>
            </Box>

            <Collapse in={!assistantDialog.isMinimized}>
                <Box
                    sx={{
                        maxHeight: "calc(70vh - 80px)",
                        overflowY: "auto",
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        backgroundColor: "#f8fafc",
                    }}
                >
                    {sortedItems.map((item) => (
                        <AssistantItem key={item.id} item={item} />
                    ))}
                </Box>
            </Collapse>
        </Paper>
    );
};

